import { useCallback, useEffect, useState } from 'react';
import { Inbox, Loader2, CircleCheck, CircleX, Download } from 'lucide-react';
import { useCompany } from '@/context/CompanyContext';
import { usePermissions } from '@/hooks/usePermissions';
import { emitToast } from '@/components/ui/Toast';
import { interfacesService, describeOrdersRun, type ErpWarehouse, type InterfaceOrder, type InterfaceOrderStatus } from '@/lib/interfacesService';
import { DocumentList, PendingOrderCard } from './PendingOrderCard';

type Tab = 'pending' | 'history';
const TAB_STATUSES: Record<Tab, InterfaceOrderStatus[]> = {
  pending: ['pending_manual', 'failed'],
  history: ['processed', 'cancelled'],
};

export function InterfacesPendingOrdersPage() {
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const { can } = usePermissions();
  const canEdit = can({ module: 'interfaces', level: 'editor' });
  const [tab, setTab] = useState<Tab>('pending');
  const [orders, setOrders] = useState<InterfaceOrder[]>([]);
  const [warehouses, setWarehouses] = useState<ErpWarehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pulling, setPulling] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    try {
      const [list, wh] = await Promise.all([
        interfacesService.listOrders(companyId, TAB_STATUSES[tab]),
        interfacesService.getWarehouses(companyId),
      ]);
      setOrders(list); setWarehouses(wh.list);
    } catch (e) {
      console.error('Failed to load interface orders:', e);
      setError('לא הצלחנו לטעון את ההזמנות');
    } finally {
      setLoading(false);
    }
  }, [companyId, tab]);

  useEffect(() => { load(); }, [load]);

  const handleDone = (message: string) => { emitToast(message, 'success'); load(); };

  const pullNow = async () => {
    if (!companyId || pulling) return;
    setPulling(true);
    const r = await interfacesService.runOrdersNow(companyId);
    setPulling(false);
    if (!r.ok) { emitToast(r.error, 'error'); return; }
    emitToast(describeOrdersRun(r.data), 'success');
    load();
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6" dir="rtl">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">הזמנות ממתינות לטיפול</h1>
          <p className="mt-1 text-gray-600">הזמנות שנעצרו כי חסר מלאי או שההפקה נכשלה. בחרו מחסן לכל פריט ושדרו להפקה.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
        {canEdit && (
          <button
            onClick={pullNow}
            disabled={pulling || !companyId}
            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-sky-700 disabled:opacity-60"
            title="המערכת בודקת אוטומטית כל רבע שעה — כאן אפשר לבדוק מיד"
          >
            {pulling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {pulling ? 'מושך הזמנות...' : 'משוך הזמנות עכשיו'}
          </button>
        )}
        <div className="flex rounded-lg border border-gray-200 bg-white p-1">
          {(['pending', 'history'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === t ? 'bg-sky-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {t === 'pending' ? 'ממתינות' : 'טופלו'}
            </button>
          ))}
        </div>
        </div>
      </header>

      {loading || !companyId ? (
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center rounded-2xl border border-gray-200 bg-white px-6 py-16 text-center shadow-sm">
          <div className="rounded-full bg-emerald-50 p-4 text-emerald-500"><Inbox className="h-8 w-8" /></div>
          <p className="mt-4 font-semibold text-gray-900">{tab === 'pending' ? 'אין הזמנות שממתינות לטיפול' : 'עוד אין הזמנות שטופלו'}</p>
          <p className="mt-1 text-sm text-gray-500">{tab === 'pending' ? 'כשהזמנה תיעצר, תקבלו התראה והיא תופיע כאן.' : 'הזמנות שהופקו להן מסמכים יופיעו כאן.'}</p>
        </div>
      ) : tab === 'pending' ? (
        <div className="space-y-3">
          {orders.map(o => (
            <PendingOrderCard key={o.id} order={o} companyId={companyId} warehouses={warehouses} canEdit={canEdit} onDone={handleDone} />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(o => (
            <div key={o.id} className="rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-gray-900">הזמנה {o.order_name}</p>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  o.status === 'processed' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {o.status === 'processed' ? <CircleCheck className="h-3.5 w-3.5" /> : <CircleX className="h-3.5 w-3.5" />}
                  {o.status === 'processed' ? 'טופלה' : 'בוטלה ללא מסמך'}
                </span>
              </div>
              {o.interface_order_documents.length ? <DocumentList docs={o.interface_order_documents} /> : o.reason && <p className="text-sm text-gray-500">{o.reason}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
